import React, { memo, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from "react";
import JoditEditor from "jodit-react";
import "../../stylesheets/TextEditor.css";
import debounce from "lodash.debounce";

interface TextEditorProps {
  onChange: (value: string) => void;
  value?: string;
  lightTheme?: "Light" | "Dark";
  height?: string;
}

export interface TextEditorRef {
  getCurrentValue: () => string;
}

const TextEditor = forwardRef<TextEditorRef, TextEditorProps>(({
  onChange,
  value = "",
  lightTheme = "Light",
  height = "100px",
}, ref) => {
  const editorRef = useRef<any>(null);
  const currentValueRef = useRef<string>(value);

  // Function to clean and replace unwanted spans
  const cleanAndReplaceMetadata = (html: string) => {
    if (!html) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Remove spans with data-metadata attributes and replace them with placeholder text
    const unwantedSpans = doc.querySelectorAll(
      "[data-metadata], [data-buffer]"
    );
    unwantedSpans.forEach((span) => {
      span.remove(); // Remove the unwanted span entirely
    });

    // Get the cleaned content (HTML without unwanted spans)
    return doc.body.innerHTML; // If you want the HTML without unwanted spans
  };

  // Expose method to get current value immediately (bypassing debounce)
  useImperativeHandle(ref, () => ({
    getCurrentValue: () => {
      return currentValueRef.current;
    }
  }));

  // Cleaning the value once before logging
  const cleanedValue = cleanAndReplaceMetadata(value);

  // Editor configuration — memoized so a new object isn't created on every
  // parent render, which would cause Jodit to re-mount and blink.
  const config = useMemo(() => ({
    theme: lightTheme.toLowerCase(),
    height: height,
    readonly: false,
    disablePlugins: "add-new-line",
    toolbar: true,
    minHeight: "120px",
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    allowResizeY: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    toolbarAdaptive: false,
    buttons: [
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "align",
      "|",
      "fontsize",
      "brush",
      "|",
      "undo",
      "redo",
    ],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lightTheme, height]);

  const editorStyle: React.CSSProperties = {
    borderRadius: "12px",
    overflow: "hidden",
  };

  const debouncedOnChange = useCallback(
    debounce((newContent: string) => {
      if (typeof newContent === "string") {
        const newValue = cleanAndReplaceMetadata(newContent);
        currentValueRef.current = newValue; // Update ref immediately
        onChange(newValue);
      }
    }, 2200),
    [onChange]
  );

  // Handle immediate change to update ref without debounce
  const handleEditorChange = useCallback((newContent: string) => {
    if (typeof newContent === "string") {
      const newValue = cleanAndReplaceMetadata(newContent);
      currentValueRef.current = newValue; // Update ref immediately
      debouncedOnChange(newContent); // Call debounced onChange
    }
  }, [debouncedOnChange]);

  return (
    <div style={editorStyle}>
      <JoditEditor
        ref={editorRef}
        value={cleanedValue}
        config={config}
        onChange={handleEditorChange}
      />
    </div>
  );
});

export default memo(TextEditor);
